import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CreditCard } from 'lucide-angular';
import { finalize } from 'rxjs';
import { Banner } from '../../ui/banner/banner';
import { OrganizationService } from '../../../services/organization/organization.service';
import { BillingService } from '../../../services/billing/billing.service';

// App-wide nudge shown while the organization is on a trial with no card on
// file: access ends when the trial does. The CTA opens the Stripe Customer
// Portal (via BillingService) where the user adds a payment method.
@Component({
  selector: 'skk-trial-banner',
  imports: [Banner],
  templateUrl: './trial-banner.html',
  styleUrl: './trial-banner.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrialBanner {
  private readonly organizationService = inject(OrganizationService);
  private readonly billingService = inject(BillingService);

  // Only nudge in the final stretch of the trial - the "14 days, no card
  // required" promise shouldn't be undercut by an immediate payment banner.
  private static readonly SHOW_WITHIN_DAYS = 5;

  readonly CreditCardIcon = CreditCard;
  readonly isLoading = signal(false);

  private readonly subscription = computed(
    () => this.organizationService.organization()?.subscription,
  );

  readonly show = computed(() => {
    const sub = this.subscription();
    if (sub?.status !== 'trialing' || sub.trialHasPaymentMethod) return false;
    const days = this.daysRemaining();
    return days === null || days <= TrialBanner.SHOW_WITHIN_DAYS;
  });

  readonly daysRemaining = computed(() => {
    const trialEndsAt = this.subscription()?.trialEndsAt;
    if (!trialEndsAt) return null;
    const diff = new Date(trialEndsAt).getTime() - Date.now();
    // trialEndsAt is offset +1 day on the backend for Stripe; cap at 14.
    return Math.min(14, Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24))));
  });

  addPaymentMethod(): void {
    this.isLoading.set(true);
    this.billingService
      .createPortalSession({ returnUrl: window.location.href })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(({ url }) => {
        window.location.href = url;
      });
  }
}
